/**
 * Standalone data for the WhatsInEachAnalysis page
 * This is a self-contained data structure that can be easily edited
 * without affecting other parts of the application
 */
export default function GetWhatsInAnalysisData() {

    const analysisData = [
        {
            stage: 1,
            title: "Understand Which Climate Risks You're Up Against",
            content: 'Find out which climate risks threaten your wallet the most, from flood and wildfire to wind, heat, or storm exposure. Your results are personalized to your home and location, showing exactly where to focus your attention and why it matters.',
            details: [],
            productImages: [
                {
                    image: "quoll-product-risks-in-your-area.webp",
                    alt: "Risks in Your Area",
                    description: "Get a baseline of the regional hazards that may affect your home."
                },
                {
                    image: "quoll-product-risks-to-focus-on.webp",
                    alt: "Risks to Focus On",
                    description: "Identify which specific risks matter most for your property."
                },
                // {
                //     image: "quoll-product-risks-mapped-to-house.webp",
                //     alt: "Risks Mapped to Your Home",
                //     description: "See which parts of your home are most exposed."
                // },
                // {
                //     image: "quoll-product-risks-financial-impact.webp",
                //     alt: "Financial Impact of Risks",
                //     description: "Estimate how each risk could affect short-term costs, recurring expenses, and long-term value."
                // }
            ]
        },
        {
            stage: 2,
            title: 'See How These Risks Translate Into Costs',
            content: "Understand how climate risks impact your insurance, taxes, and maintenance expenses, and how those costs could grow if left unchecked. See how your home's financial exposure compares to your community, and what steps can help protect your bottom line.",
            details: [
                {
                    id: "stage-2-1",
                    icon: 'broken-home-green.png',
                    timeframe: 'Sudden',
                    title: 'Unforeseen Fixes',
                    description: "Costs you may not have budgeted for.",
                    descriptionMobile: "Costs you may not have budgeted for."
                },
                {
                    id: "stage-2-2",
                    icon: 'home_bills.png',
                    timeframe: 'Recurring',
                    title: 'General Expenses',
                    description: 'Think insurance, maintenance, taxes, utilities etc.',
                    descriptionMobile: 'Insurance, utilities, maintenance etc.'
                },
                {
                    id: "stage-2-3",
                    icon: 'home-ltv-icon.png',
                    timeframe: 'Long-Term',
                    title: 'Future Market Value',
                    description: 'What happens when you go to sell the property?',
                    descriptionMobile: 'What happens when you sell the property?'
                }
            ],
            productImages: [
                {
                    image: "quoll-product-risks-financial-impact.webp",
                    alt: "Financial Impact of Risks",
                    description: "Estimate how each risk could affect short-term costs, recurring expenses, and long-term value."
                },
                {
                    image: "quoll-product-risks-mapped-to-house.webp",
                    alt: "Risks Mapped to Your Home",
                    description: "See which parts of your home are most exposed."
                }
            ]
        },
        {
            stage: 3,
            title: 'Learn the Home Improvements That Lower Your Risk and Pay You Back',
            content: "Explore home resilience upgrades that strengthen your property, reduce insurance premiums, and increase your home's long-term value. Each recommendation is tailored to your property's vulnerabilities and comes with estimated savings and available incentives.",
            details: [
                {
                    id: "stage-3-1",
                    icon: 'solving_for_icon.svg',
                    timeframe: 'Understand',
                    title: "Know What You're Solving For",
                    description: "Know exactly what each project is meant to achieve"
                },
                {
                    id: "stage-3-2",
                    icon: 'cost_benefit_analysis_icon.svg',
                    timeframe: 'Make a Decision',
                    title: 'Cost Benefit Analysis + Discounts',
                    description: 'See how each project benefits your financially'
                },
                {
                    id: "stage-3-3",
                    icon: 'guidance_on_what_to_do.svg',
                    timeframe: 'Get It Done',
                    title: 'Step-By-Step Guidance + Support',
                    description: 'Leverage DIY resources and connections to local professionals'
                }
            ],
            productImages: [
                {
                    image: "quoll-product-projects-categorized.webp",
                    alt: "Project Categories",
                    description: "Explore projects organized by risk type (wind, flood, heat, etc.)."
                },
                {
                    image: "quoll-product-project-view.webp",
                    alt: "Project Breakdown",
                    description: "See cost, complexity, and expected benefits for each."
                },
                {
                    image: "quoll-product-projects-value.webp",
                    alt: "Compare Projects",
                    description: "Compare based on savings, ROI, or level of protection."
                },
                {
                    image: "quoll-product-project-combinations.webp",
                    alt: "Project Combinations",
                    description: "Model how projects work together to lower overall risk."
                }
            ]
        },
        {
            stage: 4,
            title: "Get Connected to Local Pros or Utilize Our DIY Resources",
            content: "Get step-by-step guidance and tools to choose the right resilience projects, find qualified contractors, and complete your upgrades efficiently. Quoll supports you from planning to completion, helping you protect your home and build financial stability in a changing climate.",
            details: [
                {
                    id: "stage-4-1",
                    icon: 'engaging-pros.svg',
                    timeframe: '',
                    title: 'Connect with Local Pros',
                    description: ''
                },
                {
                    id: "stage-4-2",
                    icon: 'financing-options.svg',
                    timeframe: '',
                    title: "Understand Financing Options",
                    description: ""
                },
                {
                    id: "stage-4-3",
                    icon: 'companies-bidding.svg',
                    timeframe: '',
                    title: 'Get Help With Obtaining Discounts',
                    description: ''
                },
                {
                    id: "stage-4-4",
                    icon: 'diy-guidance-tools.svg',
                    timeframe: '',
                    title: 'DIY Materials List + Guides',
                    description: ''
                }
            ],
            productImages: [
                {
                    image: "quoll-product-taking-action-create-plan.webp",
                    alt: "Create a Plan",
                    description: "Build your own resilience roadmap."
                },
                {
                    image: "quoll-product-taking-action-get-discounts.webp",
                    alt: "Get Discounts",
                    description: "Access available incentives or rebates."
                },
                {
                    image: "quoll-product-taking-action-diy-help.webp",
                    alt: "DIY Help",
                    description: "Learn how to complete simple projects yourself."
                },
                {
                    image: "quoll-product-taking-action-connect-with-professionals.webp",
                    alt: "Professional Help",
                    description: "Connect with vetted experts for complex jobs."
                },
                {
                    image: "quoll-product-taking-action-quoll-to-market-for-you.webp",
                    alt: "Let Quoll Manage It",
                    description: "Prefer a hands-off approach? Let us help coordinate the process."
                }
            ]
        }
    ];

    return analysisData;
}
